import { DropdownMenu } from "radix-ui";
import { Ellipsis } from "lucide-react";
import { forwardRef, type ComponentProps, type ReactNode } from "react";
import { IconButton } from "./Button.js";
import { cx } from "./cx.js";

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export interface MenuContentProps {
  children: ReactNode;
  align?: "start" | "center" | "end";
  onCloseAutoFocus?: ComponentProps<typeof DropdownMenu.Content>["onCloseAutoFocus"];
}

export function MenuContent({ children, align = "end", onCloseAutoFocus }: MenuContentProps) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content className="menu__content" align={align} sideOffset={6} onCloseAutoFocus={onCloseAutoFocus}>
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

export interface OverflowMenuProps {
  label: string;
  children: ReactNode;
  disabled?: boolean;
  onCloseAutoFocus?: MenuContentProps["onCloseAutoFocus"];
}

/** Compact actions menu with an accessible, ref-addressable trigger. */
export const OverflowMenu = forwardRef<HTMLButtonElement, OverflowMenuProps>(function OverflowMenu(
  { label, children, disabled, onCloseAutoFocus },
  ref,
) {
  return (
    <Menu>
      <MenuTrigger asChild disabled={disabled}>
        <IconButton ref={ref} icon={Ellipsis} label={label} size="sm" disabled={disabled} />
      </MenuTrigger>
      <MenuContent onCloseAutoFocus={onCloseAutoFocus}>{children}</MenuContent>
    </Menu>
  );
});

export function MenuItem({
  children,
  onSelect,
  danger,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <DropdownMenu.Item
      className={cx("menu__item", danger && "menu__item--danger")}
      onSelect={onSelect}
      disabled={disabled}
    >
      {children}
    </DropdownMenu.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className="menu__label">{children}</DropdownMenu.Label>;
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="menu__separator" />;
}
